import { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Tabs, TabItem, Button } from 'ypf-pas-general-ui-components';
import { BsFillBellFill, BsFillExclamationCircleFill } from 'react-icons/bs';
import { useParams } from 'react-router-dom';

import LoaderContext from '../../../../context/LoaderProvider';
import { checkRol } from '../../../../security';
import { proveedorNotificador, proveedorAdministrador } from '../../../../security/constantsRols';
import FloatingButton from '../../../../components/FloatingButton/FloatingButton';
import CommunicateToContainer from '../../container/CommunicateToContainer/CommunicateToContainer';
import WorkReports from '../../../../components/WorkReports/WorkReports';
import { assignmentTypeEnum, statusEnum } from '../../../../utils/dataStatus';
import { getRequestTo } from '../../../../services/serviceRequest';
import { DetailPermissions } from '../../../../components/MonthlyServiceRequest/RequestDetailTypes';
import { DetailRequest, DetailRequestDefaultProp } from '../../../../utils/globalPropTypes';
import ApproverContainer from '../../container/ApproverContainer/ApproverContainer';
import ComponentWithTooltip from '../../../../components/ComponentWithTooltip/ComponentWithTooltip';
import {
  postCalendarData,
  postPendingAuthorization
} from '../../../../adapters/requestServiceDetails/requestServiceDetailsAdapter';
import { postActionsInspector } from '../../../../services/certificationsMaster';
import useToast from '../../../../hooks/useToast';

import PteAutorizar from './PteAutorizar/PteAutorizar';
import RecordHistoryContainer from './RecordHistory/RecordHistoryContainer';
import FilesDetailServiceRequest from './FilesDetailServiceRequest/FilesDetailServiceRequest';
import Messages from './Messages/Messages';
import SolicitatedTab from './SolicitatedTab/SolicitatedTab';
import ButtonTab from './SolicitatedTab/ButtonTab';

import './TabsDetail.scss';

const isTabByStatus = (idStatus) =>
  idStatus === statusEnum.asignada ||
  idStatus === statusEnum.enEjecucion ||
  idStatus === statusEnum.finalizada ||
  idStatus === statusEnum.cancelada;

const isDisabledEditStateCancelOrFinish = (idStatus) =>
  idStatus === statusEnum.finalizada || idStatus === statusEnum.cancelada;

const getTitleStatus = (idStatus) =>
  isTabByStatus(idStatus) ? 'PARTE DE TRABAJO' : 'SOLICITADO A';

const TabsDetail = ({ idStatus, details, activeTab, setActiveTab, permissions, isOyM }) => {
  const tabByStatus = isTabByStatus(idStatus);

  const disabledEditStateCancelOrFinish = isDisabledEditStateCancelOrFinish(idStatus);

  const titleStatus = getTitleStatus(idStatus);
  const [providerDetails, setProviderDetails] = useState({});
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [updateNewMessage, setUpdateNewMessage] = useState(false);
  const [hasNewAttachedFile, setHasNewAttachedFile] = useState(details.nuevoAdjunto);
  const [pendingAuthorizationData, setPendingAuthorizationData] = useState(null);
  const [selectedPendingLines, setSelectedPendingLines] = useState([]);
  const [authorizationError, setAuthorizationError] = useState('');
  const [isPendingAuthorization, setIsPendingAuthorization] = useState(false);
  const [calendarData, setCalendarData] = useState(null);

  const { id } = useParams();
  const { setLoader } = useContext(LoaderContext);
  const { setNewToast } = useToast();

  // Keeps in this component for use it in authorize action

  const fetchPendingAuthorizationData = useCallback(
    async (fechaDesde, fechaHasta) => {
      try {
        setLoader(true);
        // Fetch pendind authorization data

        const body = {
          id: Number(id) || details?.id,
          fechaDesde: fechaDesde || null,
          fechaHasta: fechaHasta || null,
          contractNumber: details?.numeroContrato
        };

        const responseData = await postPendingAuthorization(body);

        setPendingAuthorizationData(responseData);

        // Set all lines of response as selected
        setSelectedPendingLines(responseData.lineas);

        // Fetch calendar data
        const calendarBody = {
          id: Number(id) || details?.id,
          contractNumber: details?.numeroContrato
        };
        const calendarDataResponse = await postCalendarData(calendarBody);

        setCalendarData(calendarDataResponse);
      } catch (err) {
        setPendingAuthorizationData({ mensaje: err.mensaje || err.data?.message });
      } finally {
        setLoader(false);
      }
    },
    [id, details?.id, details?.numeroContrato, setLoader]
  );

  const authorizeSelectedPendingLines = useCallback(async () => {
    try {
      const idsToAuthorize = selectedPendingLines.flatMap(
        (line) => line.idParteTrabajoDiariosCostos
      );

      const response = await postActionsInspector({
        action: 'Autorizar',
        parteDiarioCostoIds: idsToAuthorize
      });

      setNewToast({
        type: 'SUCCESS',
        message: response?.message || 'Se autorizaron las líneas seleccionadas'
      });
      await fetchPendingAuthorizationData();
    } catch (err) {
      setAuthorizationError(err?.data?.message);
      setNewToast({
        type: 'ERROR',
        message: err?.data?.message
      });
      await fetchPendingAuthorizationData(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPendingAuthorizationData, selectedPendingLines]);

  useEffect(() => {
    if (details) {
      setHasNewMessage(details.nuevoMensaje);
      if (checkRol(proveedorAdministrador))
        getRequestTo(id).then((res) => setProviderDetails(res[0]));
    }
  }, [details, id]);

  useEffect(() => {
    if (updateNewMessage) {
      setHasNewMessage(false);
    }
  }, [updateNewMessage]);

  useEffect(() => {
    setHasNewAttachedFile(details.nuevoAdjunto);
  }, [details.nuevoAdjunto]);

  const showExclamation = () => (
    <ComponentWithTooltip tooltipText="Nueva documentación adjunta">
      <BsFillExclamationCircleFill
        style={{ display: 'flex', marginLeft: '5px', color: '#ff973d' }}
      />
    </ComponentWithTooltip>
  );

  const seeAttachedFile = () => {
    setHasNewAttachedFile(false);
  };

  return (
    <>
      {details && permissions ? (
        <>
          <Tabs activeKey={activeTab} className="mb-5" defaultActiveKey="solicitado">
            <TabItem eventKey="solicitado" title={`${titleStatus}`}>
              {tabByStatus ? (
                <WorkReports
                  asignationType={details?.tipoAsignacion?.id}
                  contractNumber={details?.numeroContrato}
                  details={details}
                  disabledEditStateCancelOrFinish={disabledEditStateCancelOrFinish}
                  filesPermissions={permissions.attachedFiles}
                  isASDS={details?.tipoAsignacion?.id === assignmentTypeEnum.Automatica}
                  isOyM={isOyM}
                  notificaPP={details?.contratoNotificaPP}
                  setIsPendingAuthorization={setIsPendingAuthorization}
                  typeOfUbication={details?.tipoUbicacion}
                  workPartPermissions={permissions.workPart}
                />
              ) : (
                <SolicitatedTab
                  details={details}
                  hasPermissionToAddProvider={permissions?.workPart?.addProvider}
                  setActiveTab={setActiveTab}
                  setIsPendingAuthorization={setIsPendingAuthorization}
                />
              )}
            </TabItem>
            {details?.certificaEnYclick && permissions.pendingAuthorization?.visualize && (
              <TabItem eventKey="autorizar" title="A AUTORIZAR">
                <PteAutorizar
                  arrayDate={calendarData?.calendarDates}
                  authorizationError={authorizationError}
                  data={pendingAuthorizationData}
                  fetchData={fetchPendingAuthorizationData}
                  maxDate={calendarData?.lastDate}
                  minDate={calendarData?.firstDate}
                  selectedLines={selectedPendingLines}
                  serviceDetails={details}
                  setAuthorizationError={setAuthorizationError}
                  setIsPendingAuthorization={setIsPendingAuthorization}
                  setSelectedLines={setSelectedPendingLines}
                />
              </TabItem>
            )}
            <TabItem
              eventKey="mensaje"
              icon={hasNewMessage ? BsFillBellFill : null}
              placementIcon="right"
              styleIcon={{
                color: '#ff973d'
              }}
              title="MENSAJES"
            >
              {permissions?.message && (
                <Messages
                  idStatus={idStatus}
                  messagePermissions={permissions?.message}
                  setIsPendingAuthorization={setIsPendingAuthorization}
                  setUpdateNewMessage={setUpdateNewMessage}
                />
              )}
            </TabItem>
            <TabItem eventKey="aprobadores" title="APROBADORES">
              <ApproverContainer
                aprroverPermissions={permissions.approvers}
                details={details}
                setIsPendingAuthorization={setIsPendingAuthorization}
              />
            </TabItem>
            <TabItem eventKey="comunicar" title="SEGUIMIENTO">
              <CommunicateToContainer
                communicateToPermissions={permissions.communicateTo}
                details={details}
                setIsPendingAuthorization={setIsPendingAuthorization}
              />
            </TabItem>
            <TabItem
              eventKey="archivos"
              icon={hasNewAttachedFile ? showExclamation : null}
              title="ARCHIVOS"
            >
              <FilesDetailServiceRequest
                details={details}
                filesPermissions={permissions.attachedFiles}
                idStatus={idStatus}
                seeAttachedFile={seeAttachedFile}
                setIsPendingAuthorization={setIsPendingAuthorization}
              />
            </TabItem>
            {/* {
              !(checkRol(proveedorNotificador) || checkRol(proveedorAdministrador)) &&  */}
            <TabItem
              disabled={checkRol(proveedorNotificador) || checkRol(proveedorAdministrador)}
              eventKey={
                !(checkRol(proveedorNotificador) || checkRol(proveedorAdministrador))
                  ? 'historial'
                  : ''
              }
              title={
                !(checkRol(proveedorNotificador) || checkRol(proveedorAdministrador))
                  ? 'HISTORIAL'
                  : ''
              }
            >
              <RecordHistoryContainer setIsPendingAuthorization={setIsPendingAuthorization} />
            </TabItem>
            {/* } */}
          </Tabs>

          <div className="d-flex justify-content-center align-item-center content-buttons-tabs">
            <ButtonTab
              accepted={providerDetails?.aceptado}
              dates={{
                realizationDate: details.fechaProbableRealizacion,
                responseDate: details.fechaRespuesta
              }}
              idStatus={idStatus}
              key={`${idStatus}${providerDetails?.aceptado}`}
              sdsName={details.servicio.nombre}
              sdsNumber={details.numero}
              showFinalizeButton={details.botonFinalizarServicio}
            />
            {details?.certificaEnYclick &&
              permissions.pendingAuthorization?.visualize &&
              isPendingAuthorization && (
                <Button
                  disabled={
                    !pendingAuthorizationData?.permitirAutorizacion ||
                    selectedPendingLines?.length <= 0
                  }
                  key="buttonAutorizar"
                  label="Autorizar"
                  onClick={authorizeSelectedPendingLines}
                  type="button"
                  variant="outline-success"
                />
              )}
          </div>
          <FloatingButton isOpen>
            <ButtonTab
              accepted={providerDetails?.aceptado}
              dates={{
                realizationDate: details.fechaProbableRealizacion,
                responseDate: details.fechaRespuesta
              }}
              idStatus={idStatus}
              sdsName={details.servicio.nombre}
              sdsNumber={details.numero}
              showFinalizeButton={details.botonFinalizarServicio}
            />
          </FloatingButton>
        </>
      ) : null}
    </>
  );
};

TabsDetail.propTypes = {
  details: DetailRequest,
  idStatus: PropTypes.number,
  permissions: DetailPermissions.isRequired,
  activeTab: PropTypes.string,
  setActiveTab: PropTypes.func,
  isOyM: PropTypes.bool
};

TabsDetail.defaultProps = {
  details: DetailRequestDefaultProp,
  idStatus: 1,
  setActiveTab: () => {},
  activeTab: '',
  isOyM: false
};

export default TabsDetail;
